import React, { useEffect, useMemo, useReducer, useState } from 'react';
import {
  AutocompleteComponent,
  DatePickerComponent,
  DialogComponent,
  Inputs,
  SelectComponet,
  SwitchComponent,
} from '../../../../../../../Components';
import {
  getErrorByName,
  GetParams,
  showError,
  showSuccess,
} from '../../../../../../../Helper';
import { offerSchema } from './JoiSchema';
import { useTranslation } from 'react-i18next';
import ServicesAutocomplete from './components/ServicesAutocomplete';
import { ImageUploader } from '../../../../../../../SharedComponents/ImageUploader/ImageUploader';
import LookupAutocomplete from '../../../../../../../Components/Controls/LookupAutocomplete/LookupAutocomplete';
import {
  CreateVendorOffer,
  UpdateVendorOffer,
} from '../../../../../../../Services';
import moment from 'moment';
import { mapActiveItemToState, mapStateToActiveItem } from './model';
import { useFetchVendorCategories } from '../../hooks/fetchVendorCategories';
import { useFetchVendors } from '../../hooks/fetchVendors';

const reducer = (state, action) => {
  if (action.id === 'edit') {
    return action.value;
  }
  if (action.id === 'edit-some') {
    return {
      ...state,
      ...action.value,
    };
  }
  state[action.id] = action.value;
  return { ...state };
};

const defaultState = () => ({
  offerName: '',
  vendorServiceId: null,
  vendorId: +GetParams('id'),
  offerStartDate: null,
  offerEndDate: null,
  offerWithDuration: false,
  offerDuration: null,
  description: '',
  discountPercentage: null,
  withPSICommission: false,
  psiCommissionPercentage: null,
  offerAvailableOn: null,
  promoCode: '',
  qrCode: '',
  qrFileId: null,
  offerStatusId: null,
  isPublishOffer: false,
  vendorServiceCategoryIds: [],
  selectedCategories: null,
  selectedService: null,
});
const offerAvailabilityOptions = [
  { title: 'Online', value: 1 },
  { title: 'On Site', value: 2 },
  { title: 'Both', value: 3 },
];
const OfferManagementDialog = ({
  parentTranslationPath,
  open,
  onClose,
  reload,
  activeItem,
}) => {
  const { t } = useTranslation(parentTranslationPath);
  const { categories, setVendorId, isLoading } = useFetchVendorCategories(
    defaultState().vendorId
  );
  const {
    vendors,
    getVendorsAdvanceSearch,
    isLoading: isLoadingVendors,
  } = useFetchVendors();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [state, dispatch] = useReducer(reducer, {
    ...defaultState(),
  });
  const schema = offerSchema(t).validate(state);

  const displayVendor = useMemo(() => {
    if (!defaultState().vendorId && !activeItem) return true;
    if (activeItem) return false;
    else return false;
  }, [activeItem]);

  const saveHandler = async (event) => {
    event.preventDefault();
    setIsSubmitted(true);
    if (schema.error) {
      showError(t('Shared:please-fix-all-errors'));
      return;
    }
    try {
      if (!activeItem) {
        await CreateVendorOffer(mapStateToActiveItem(state));
        showSuccess(t('offer_created_successfully'));
      } else {
        await UpdateVendorOffer(
          activeItem.vendorOfferId,
          mapStateToActiveItem(state)
        );
        showSuccess(t('offer_updated_successfully'));
      }
      reload();
      onClose();
    } catch (error) {
      showError(t('something_went_wrong'));
    }
  };
  const onCloseHandler = () => {
    dispatch({ id: 'edit', value: defaultState() });
    onClose();
  };
  useEffect(() => {
    if (activeItem) {
      dispatch({ id: 'edit', value: mapActiveItemToState(activeItem) });
    }
  }, [activeItem]);

  useEffect(() => {
    if (state.offerStartDate && state.offerEndDate) {
      dispatch({
        id: 'offerDuration',
        value: moment(state.offerEndDate).diff(state.offerStartDate, 'days'),
      });
    } else {
      dispatch({
        id: 'offerDuration',
        value: null,
      });
    }
  }, [state.offerStartDate, state.offerEndDate]);

  return (
    <DialogComponent
      titleText={activeItem ? 'edit_offer' : 'add_offer'}
      parentTranslationPath={parentTranslationPath}
      isOpen={open}
      onCancelClicked={onCloseHandler}
      onCloseClicked={onCloseHandler}
      maxWidth={'sm'}
      dialogContent={
        <div className=' flex gap-1 d-flex-column'>
          <Inputs
            labelValue={'offer_name'}
            onInputChanged={(e) =>
              dispatch({ id: 'offerName', value: e.target.value })
            }
            value={state.offerName}
            parentTranslationPath={parentTranslationPath}
            isWithError
            error={getErrorByName(schema, 'offerName').error}
            helperText={getErrorByName(schema, 'offerName').message}
            isSubmitted={isSubmitted}
          />
          {displayVendor && (
            <AutocompleteComponent
              data={vendors}
              labelValue={'vendor'}
              onOpen={() => getVendorsAdvanceSearch()}
              multiple={false}
              displayLabel={(option) => option.name ?? ''}
              isLoading={isLoadingVendors}
              getOptionSelected={(option, value) => option.id === value.id}
              withoutSearchButton
              onChange={(_e, newValue) => {
                setVendorId(newValue?.id);
                dispatch({
                  id: 'edit-some',
                  value: {
                    vendorId: newValue?.id,
                    selectedCategories: null,
                    selectedService: null,
                    vendorServiceCategoryIds: [],
                    vendorServiceId: null,
                  },
                });
              }}
              parentTranslationPath={parentTranslationPath}
              onInputChange={(_e, value) => {
                getVendorsAdvanceSearch(value);
              }}
            />
          )}
          <AutocompleteComponent
            data={categories}
            labelValue={'service_category'}
            getOptionSelected={(option, value) =>
              option.serviceCategoryId === value.serviceCategoryId
            }
            isLoading={isLoading}
            isSubmitted={isSubmitted}
            parentTranslationPath={parentTranslationPath}
            selectedValues={state.selectedCategories}
            displayLabel={(option) => option.serviceCategoryName}
            chipsLabel={(option) => option.serviceCategoryName}
            withoutSearchButton
            multiple={false}
            onChange={(_e, newValue) => {
              dispatch({
                id: 'edit-some',
                value: {
                  vendorServiceCategoryIds: newValue?.serviceCategoryId
                    ? [newValue?.serviceCategoryId]
                    : [],
                  selectedCategories: newValue,
                  selectedService: null,
                  vendorServiceId: null,
                },
              });
            }}
            isWithError
            error={getErrorByName(schema, 'vendorServiceCategoryIds').error}
            helperText={
              getErrorByName(schema, 'vendorServiceCategoryIds').message
            }
          />
          <ServicesAutocomplete
            onChange={(_e, newValue) => {
              dispatch({
                id: 'edit-some',
                value: {
                  vendorServiceId: newValue?.vendorServiceId,
                  selectedService: newValue,
                },
              });
            }}
            vendorId={state.vendorId}
            selectedService={state.selectedService}
            selectedCategory={state.vendorServiceCategoryIds[0]}
            isWithError
            error={getErrorByName(schema, 'vendorServiceId').error}
            helperText={getErrorByName(schema, 'vendorServiceId').message}
          />
          <SwitchComponent
            themeClass={'theme-line'}
            isChecked={state.offerWithDuration}
            onChangeHandler={(_e, checked) =>
              dispatch({ id: 'offerWithDuration', value: checked })
            }
            label={'offer_with_duration'}
            labelPlacement={'start'}
            parentTranslationPath={parentTranslationPath}
          />
          <DatePickerComponent
            value={state.offerStartDate}
            labelValue={'start_date'}
            onDateChanged={(newValue) => {
              dispatch({
                id: 'edit-some',
                value: {
                  offerStartDate: newValue?.format('YYYY-MM-DD'),
                  offerEndDate: null,
                },
              });
            }}
            error={getErrorByName(schema, 'offerStartDate').error}
            helperText={getErrorByName(schema, 'offerStartDate').message}
            isSubmitted={isSubmitted}
            parentTranslationPath={parentTranslationPath}
          />
          {state.offerWithDuration && (
            <>
              <DatePickerComponent
                value={state.offerEndDate}
                labelValue={'end_date'}
                onDateChanged={(newValue) => {
                  dispatch({
                    id: 'offerEndDate',
                    value: newValue?.format('YYYY-MM-DD'),
                  });
                }}
                error={getErrorByName(schema, 'offerEndDate').error}
                helperText={getErrorByName(schema, 'offerEndDate').message}
                isSubmitted={isSubmitted}
                parentTranslationPath={parentTranslationPath}
                minDate={
                  moment(state.offerStartDate).isValid() &&
                  moment(state.offerStartDate).format('YYYY-MM-DD')
                }
              />
              <Inputs
                readOnly
                labelValue={'duration'}
                value={state.offerDuration}
                parentTranslationPath={parentTranslationPath}
              />
            </>
          )}
          <Inputs
            labelValue={'description'}
            multiline
            rows={4}
            value={state.description}
            onInputChanged={(e) =>
              dispatch({ id: 'description', value: e.target.value })
            }
            parentTranslationPath={parentTranslationPath}
          />
          <Inputs
            labelValue={'discount'}
            value={state.discountPercentage}
            endAdornment={<span className='p-1'>%</span>}
            type={'number'}
            onInputChanged={(e) => {
              if (e.target.valueAsNumber < 0) {
                return;
              }
              dispatch({
                id: 'discountPercentage',
                value: e.target.valueAsNumber,
              });
            }}
            wrapperClasses={'w-50'}
            parentTranslationPath={parentTranslationPath}
            isWithError
            error={getErrorByName(schema, 'discountPercentage').error}
            helperText={getErrorByName(schema, 'discountPercentage').message}
          />
          <div className='flex gap-1 flex-wrap fa-end'>
            <SwitchComponent
              label={'psi_commission_included'}
              labelPlacement={'start'}
              isChecked={state.withPSICommission}
              onChangeHandler={(_e, checked) =>
                dispatch({ id: 'withPSICommission', value: checked })
              }
              themeClass={'theme-inline'}
              parentTranslationPath={parentTranslationPath}
            />
            {state.withPSICommission && (
              <Inputs
                wrapperClasses={'w-25'}
                labelValue={'psi_commission'}
                value={state.psiCommissionPercentage}
                type={'number'}
                endAdornment={<span className='p-1'>%</span>}
                onInputChanged={(e) => {
                  if (e.target.valueAsNumber < 0) {
                    return;
                  }
                  dispatch({
                    id: 'psiCommissionPercentage',
                    value: e.target.valueAsNumber,
                  });
                }}
                parentTranslationPath={parentTranslationPath}
                isWithError
                error={getErrorByName(schema, 'psiCommissionPercentage').error}
                helperText={
                  getErrorByName(schema, 'psiCommissionPercentage').message
                }
              />
            )}
          </div>
          <SelectComponet
            data={offerAvailabilityOptions}
            labelValue={'offer_availability'}
            textInput={'title'}
            valueInput={'value'}
            value={state.offerAvailableOn}
            parentTranslationPath={parentTranslationPath}
            onSelectChanged={(value) => {
              dispatch({
                id: 'offerAvailableOn',
                value,
              });
            }}
          />
          <Inputs
            labelValue={'promo_code'}
            value={state.promoCode}
            onInputChanged={(e) =>
              dispatch({ id: 'promoCode', value: e.target.value })
            }
            isWithError
            error={getErrorByName(schema, 'promoCode').error}
            helperText={getErrorByName(schema, 'promoCode').message}
            parentTranslationPath={parentTranslationPath}
          />
          <Inputs
            endAdornment={
              <ImageUploader
                accept={'image/*'}
                uploadedChanged={(files) => {
                  dispatch({ id: 'qrFileId', value: files?.[0]?.uuid });
                }}
                initUploadedFiles={state.files}
                multiple={false}
                isDisabled={!!state.qrCode}
              />
            }
            isDisabled={!!state.qrFileId}
            value={state.qrCode}
            onInputChanged={(e) =>
              dispatch({ id: 'qrCode', value: e.target.value })
            }
            labelValue={'qr_code'}
            parentTranslationPath={parentTranslationPath}
            helperText={t('qr_code_helper_text')}
          />
          <LookupAutocomplete
            lookupName={'OfferStatus'}
            label={'offer_status'}
            parentTranslationPath={parentTranslationPath}
            selectedValues={state.selectedStatus}
            onChange={(_e, newValue) => {
              dispatch({
                id: 'edit-some',
                value: {
                  offerStatusId: newValue?.lookupItemId,
                  selectedStatus: newValue,
                  isPublishOffer:
                    newValue?.lookupItemName?.toLowerCase() === 'active',
                },
              });
            }}
            withError
            error={getErrorByName(schema, 'offerStatusId').error}
            helperText={getErrorByName(schema, 'offerStatusId').message}
          />
          <SwitchComponent
            label={'publish_offer'}
            isChecked={state.isPublishOffer}
            onChangeHandler={(_e, checked) =>
              dispatch({ id: 'isPublishOffer', value: checked })
            }
            themeClass={'theme-inline'}
            parentTranslationPath={parentTranslationPath}
            isDisabled={
              state.selectedStatus?.lookupItemName?.toLowerCase() !== 'active'
            }
          />
        </div>
      }
      onSaveClicked={saveHandler}
    />
  );
};
export default OfferManagementDialog;
